import { Color, Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import {
  AwsCrossAccountRoleCredentials,
  ExternalIntegration,
} from 'dashboard/models/external-integration';
import { ApiModel } from 'lib/interfaces/api';

export interface FormNewDataSourceDataCsv {
  name: string;
  type: ApiModel.ExternalIntegrationDataSourceCsv;
  file?: FileWithPreview;
}

export interface FormNewDataSourceDataMysql {
  name: string;
  type: ApiModel.ExternalIntegrationDataSourceMysql;
  host: string;
  port: string;
  user: string;
  password: string;
  database: string;
}

export interface FormNewDataSourceDataSnowflake {
  name: string;
  type: ApiModel.ExternalIntegrationDataSourceSnowflake;
  account: string;
  username: string;
  password: string;
  warehouse: string;
  database: string;
  schema: string;
}

export interface FormNewDataSourceDataS3 {
  name: string;
  type: ApiModel.ExternalIntegrationDataSourceS3;
  credentials: AwsCrossAccountRoleCredentials;
  region: string;
  bucket: string;
  objectKeys: string[];
  csvPathPrefix: string;
}

export interface FormNewDataSourceDataRedshift {
  name: string;
  type: ApiModel.ExternalIntegrationDataSourceRedshift;
  host: string;
  port: string;
  user: string;
  password: string;
  database: string;
  schema: string;
}

export interface FormNewDataSourceDataGoogleSheets {
  name: string;
  type: ApiModel.ExternalIntegrationDataSourceGoogleSheets;
  spreadsheetId: string;
  refreshToken: string;
}

export type FormNewDataSourceData =
  | FormNewDataSourceDataCsv
  | FormNewDataSourceDataGoogleSheets
  | FormNewDataSourceDataMysql
  | FormNewDataSourceDataRedshift
  | FormNewDataSourceDataSnowflake
  | FormNewDataSourceDataS3;

export type NewDataSourceType =
  | ApiModel.ExternalIntegrationDataSourceCsv
  | ApiModel.ExternalIntegrationDataSourceMysql
  | ApiModel.ExternalIntegrationDataSourceSnowflake
  | ApiModel.ExternalIntegrationDataSourceS3
  | ApiModel.ExternalIntegrationDataSourceRedshift
  | ApiModel.ExternalIntegrationDataSourceGoogleSheets;

export type AddDataSourceError = string;

export type AddDataSourceResponse = ExternalIntegration | AddDataSourceError | null;

const DATA_SOURCE_TO_VALUE_FIELDS = {
  [ApiModel.ExternalIntegrationDataSourceCsv]: ['name', 'file'],
  [ApiModel.ExternalIntegrationDataSourceRedshift]: [
    'name',
    'host',
    'port',
    'user',
    'password',
    'database',
    'schema',
  ],
  [ApiModel.ExternalIntegrationDataSourceMysql]: [
    'name',
    'host',
    'port',
    'user',
    'password',
    'database',
  ],
  [ApiModel.ExternalIntegrationDataSourceGoogleSheets]: ['name', 'spreadsheetId', 'refreshToken'],
  [ApiModel.ExternalIntegrationDataSourceSnowflake]: [
    'name',
    'account',
    'username',
    'password',
    'warehouse',
    'database',
    'schema',
  ],
  [ApiModel.ExternalIntegrationDataSourceS3]: [
    'name',
    'credentials',
    'region',
    'bucket',
    'objectKeys',
  ],
};

export const pluckAttributesFromFormData = (formData: FormNewDataSourceData) => {
  const returnObj = Object.fromEntries(
    DATA_SOURCE_TO_VALUE_FIELDS[formData.type].map((key) => [
      key,
      (formData as { [key: string]: any })[key],
    ]),
  );
  // sanitize the name field
  returnObj.name = returnObj.name.trim();
  return returnObj;
};

export const PageBody = styled.div`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${Color.CoreSlate100};
`;

export const PageButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: ${Spacing[3]} ${Spacing[4]};
`;
